import path from 'path';

import React, { useState } from 'react';
import { Button, message, Modal, Upload, Space } from 'antd';
import {
    LoadingOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ScissorOutlined,
} from '@ant-design/icons';
import ReactCrop from 'react-image-crop';
import firebase from 'firebase';

import getBase64 from '../../utils/GetBase64';
import 'react-image-crop/dist/ReactCrop.css';
import Spinner from '../Spinner';

function getTeamCollection() {
    const db = firebase.firestore();
    const ref = db.collection('teams');
    return ref;
}

function getClientCollection() {
    const db = firebase.firestore();
    const ref = db.collection('clients');
    return ref;
}

function getUserCollection() {
    const db = firebase.firestore();
    const ref = db.collection('users');
    return ref;
}

function getTemplateCollection(issuerId) {
    const db = firebase.firestore();
    const ref = db
        .collection('issuers').doc(issuerId || 'strike')
        .collection('template_tags');
    return ref;
}

function getTagCollection() {
    const db = firebase.firestore();
    const ref = db.collection('tags');
    return ref;
}

function getSiteCollection() {
    const db = firebase.firestore();
    const ref = db.collection('sites');
    return ref;
}


const saveImageToStorage = async ({ id, fieldName, imageName, base64Image, fileExtn }) => {
    if (!base64Image) {
        console.log('No image to save', id, fieldName, imageName, fileExtn);
        return null;
    }

    const storageRef = firebase.storage().ref();
    const uniqueId = id || `d-${Date.now()}`;
    const imagePrefix = imageName || 'image';
    const fieldPrefix = fieldName || 'default';
    const fileExtnDefault = fileExtn || '.png';
    let contentType = 'image/png';

    switch (fileExtnDefault) {
        case '.jpg':
        case '.jpeg':
            contentType = 'image/jpeg';
            break;
        case '.png':
            contentType = 'image/png';
            break;
        default:
            contentType = 'image/png';
            break;
    }

    const fileName = uniqueId + '-' + `${fieldPrefix}` + (fileExtnDefault);
    console.log('saveImageToStorage', id, fieldPrefix, imagePrefix, fileExtnDefault, fileName);

    const teamImagesRef = storageRef.child(`public-images/${imagePrefix}-images/${fileName}`);
    
    const resultSnapshot = await teamImagesRef.putString(base64Image, 'data_url', { contentType });
    const url = await resultSnapshot.ref.getDownloadURL();

    return url;
}

const saveUrlToId = async (ref, { id, fieldName, url }) => {
    console.log('saveUrlToId', id, fieldName, url);
    if (!id) {
        return;
    }
    await ref.doc(id).set({
        [fieldName]: url,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedImageAt: firebase.firestore.FieldValue.serverTimestamp(),
    }, { merge: true });
}

const uploadImageAndSave = async (ref, { id, fieldName, imageName, base64Image, fileExtn }) => {
    const url = await saveImageToStorage({ id, fieldName, imageName, base64Image, fileExtn });
    if (id && ref && url) {
        await saveUrlToId(ref, { id, fieldName, url });
    }
    return url;
}


export const ClientImageEditor = (props) => {
    const { clientId, currentUrl, imageName: providedName } = props;
    const { client } = props;
    const { ownerId } = client;
    const fieldName = 'avatar';
    const ref = getClientCollection();
    const imageName = providedName || 'client';
    const id = clientId;

    const userRef = getUserCollection();
    const userUserImageName = 'user';

    // MARK: Make generic for each object type (can be in call back)
    const onSave = async (base64Image, fileExtn) => {
        await uploadImageAndSave(ref, { id, fieldName, imageName, base64Image, fileExtn });
        if (ownerId) {
            await uploadImageAndSave(userRef, { id: ownerId, fieldName, imageName: userUserImageName, base64Image, fileExtn });
        }
    }

    return <>
        <UploadImageEditor
            currentUrl={currentUrl}
            circle
            onSave={onSave}
            isLoggedInMemberAdmin={true}
        />
    </>;

}

export const TeamImageEditor = (props) => {
    const { teamId, currentUrl, isLoggedInMemberAdmin, imageName: providedName } = props;
    const { onUpdateImage, onLoading } = props;
    const fieldName = 'topLogo';
    const ref = getTeamCollection();
    const imageName = providedName || 'team';
    const id = teamId;

    // MARK: Make generic for each object type (can be in call back)
    const onSave = async (base64Image, fileExtn) => {
        onLoading && onLoading({ message: 'Saving new image...' });
        const url = await uploadImageAndSave(ref, { id, fieldName, imageName, base64Image, fileExtn });
        if (onUpdateImage) {
            onUpdateImage(url);
        }
        onLoading && onLoading({ message: null });
    }

    return <>
        <UploadImageEditor
            currentUrl={currentUrl}
            rectangle
            onSave={onSave}
            isLoggedInMemberAdmin={isLoggedInMemberAdmin}
        />
    </>;
}

export const TemplateTagImageEditor = (props) => {
    const { templateId, issuerId, currentUrl, isLoggedInMemberAdmin, imageName: providedName } = props;
    const { onUpdateImage } = props;
    const fieldName = 'actionText.signupImage';
    const ref = getTemplateCollection(issuerId);
    const imageName = providedName || 'template';
    const id = templateId;

    // MARK: Make generic for each object type (can be in call back)
    const onSave = async (base64Image, fileExtn) => {
        const url = await saveImageToStorage({ id, fieldName, imageName, base64Image, fileExtn });
        if (onUpdateImage) {
            onUpdateImage(url);
        }
    }

    return <>
        <UploadImageEditor
            currentUrl={currentUrl}
            rectangle
            onSave={onSave}
            isLoggedInMemberAdmin={isLoggedInMemberAdmin}
        />
    </>;
}

export const TagImageEditor = (props) => {
    const { tagId, currentUrl, imageName: providedName } = props;
    const { onUpdateImage } = props;
    const fieldName = 'avatar';
    const ref = getTagCollection();
    const imageName = providedName || 'tag';
    const id = tagId;

    // MARK: Make generic for each object type (can be in call back)
    const onSave = async (base64Image, fileExtn) => {
        const url = await uploadImageAndSave(ref, { id, fieldName, imageName, base64Image, fileExtn });
        if (onUpdateImage) {
            onUpdateImage(url);
        }
    }

    return <>
        <UploadImageEditor
            currentUrl={currentUrl}
            circle
            onSave={onSave}
            isLoggedInMemberAdmin={true}
        />
    </>;
}

export const SiteImageEditor = (props) => {
    const { siteId, currentUrl, imageName: providedName } = props;
    const { onUpdateImage } = props;
    const {  rectangle, square } = props;
    const fieldName = 'imageUrl';
    const ref = getSiteCollection();
    const imageName = providedName || 'site';
    const id = siteId;

    // MARK: Make generic for each object type (can be in call back)
    const onSave = async (base64Image, fileExtn) => {
        const url = await uploadImageAndSave(ref, { id, fieldName, imageName, base64Image, fileExtn });
        if (onUpdateImage) {
            onUpdateImage(url);
        }
    }

    return <>
        <UploadImageEditor
            currentUrl={currentUrl}
            square={!!square}
            rectangle={!!rectangle}
            onSave={onSave}
            isLoggedInMemberAdmin={true}
        />
    </>;
}

const UploadImageEditor = (props) => {
    const { isLoggedInMemberAdmin } = props;
    const { currentUrl } = props;
    const { square, circle, rectangle } = props;
    const { onSave, onUpdate } = props;

    let radius = '100%'; // circle
    radius = circle ? '100%' : radius;
    radius = square ? '10%' : radius;
    radius = rectangle ? '0' : radius;
    let aspect = rectangle ? undefined : (1 / 1);
    const h = aspect ? (100 / aspect).toFixed(0) : 60;
    const height = `${h}%`;

    const cropDefault = {
        unit: '%',
        width: 100,
        height: 100,
        x: 0,
        y: 0,
        aspect: aspect
    };

    const imageStyle = rectangle ? {
        width: '100%',
        height: 'auto',
        maxHeight: '350px',
        borderRadius: radius,
        objectFit: 'contain',
    } : {
        width: 'auto',
        height: height,
        borderRadius: radius,
        objectFit: 'cover',
    };

    const uploadStyle = rectangle ? {
        width: '240px',
        height: '150px',
    } : {};

    // const imageStyle = { width: '104px', height: '104px', borderRadius: '100%' };

    const [isDragging, setIsDragging] = useState(false);
    const [base64Image, setBase64Image] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [imageRef, setImageRef] = useState(null);
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [crop, setCrop] = useState(cropDefault);
    const [fileName, setFileName] = useState(null);
    const [fileExtn, setFileExtn] = useState(null);
    const [src, setSrc] = useState(null);

    const isCommitButtonDisabled = isDragging || loading || !imageRef || !croppedImage;

    const handleRemove = (e) => {
        if (e) e.stopPropagation();
        Modal.confirm({
            title: 'Remove Image',
            content: 'Are you sure you want to remove this banner image?',
            okText: 'Yes, Remove',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                setLoading(true);
                try {
                    setBase64Image(null);
                    setCroppedImage(null);
                    setImageRef(null);
                    if (onSave) {
                        await onSave(null, null);
                    }
                    message.success('Image removed');
                } catch (error) {
                    console.error('Error removing image:', error);
                    message.error('Could not remove image');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleRecrop = async (e) => {
        if (e) e.stopPropagation();
        const imageUrl = base64Image || currentUrl;
        if (!imageUrl) return;

        setLoading(true);
        try {
            // For Firebase Storage URLs, we need cross-origin access for the canvas.
            // Converting to a blob/local URL is a reliable way to handle this.
            const response = await fetch(imageUrl, { mode: 'cors' });
            const blob = await response.blob();
            const localUrl = URL.createObjectURL(blob);
            setSrc(localUrl);
            setVisible(true);
        } catch (error) {
            console.error('Error loading image for recrop:', error);
            // Fallback: just try setting the URL directly
            setSrc(imageUrl);
            setVisible(true);
        } finally {
            setLoading(false);
        }
    };

    const handleOk = async () => {
        if (!croppedImage) return;
        
        setLoading(true);
        getBase64(croppedImage, async (base64) => {
            setBase64Image(base64);
            setVisible(false);
            try {
                if (onSave) {
                    await onSave(base64, fileExtn);
                }
                message.success('Image updated successfully');
            } catch (error) {
                console.error('Error saving image:', error);
                message.error('Could not save image');
            } finally {
                setLoading(false);
            }
        });
    };

    const handleCancel = () => {
        setVisible(false);
        setSrc(null);
        setCroppedImage(null);
        setImageRef(null);
    };

    const onCropComplete = crop => {
        makeClientCrop(crop);
    };

    const onCropChange = (crop, percentCrop) => {
        setCrop(crop);
    };

    const makeClientCrop = async (crop, img) => {
        const targetImage = img || imageRef;
        if (targetImage && crop.width && crop.height) {
            const pixelCrop = crop.unit === '%' ? {
                x: (crop.x * targetImage.width) / 100,
                y: (crop.y * targetImage.height) / 100,
                width: (crop.width * targetImage.width) / 100,
                height: (crop.height * targetImage.height) / 100,
            } : crop;

            const croppedImage = await getCroppedImg(
                targetImage,
                pixelCrop,
            );
            setCroppedImage(croppedImage);
        }
    }

    const onImageLoaded = image => {
        setImageRef(image);
        if (crop) {
            makeClientCrop(crop, image);
        }
    };

    const getCroppedImg = (image, crop) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const pixelRatio = window.devicePixelRatio;
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
        canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

        ctx.scale(pixelRatio, pixelRatio);
        ctx.imageSmoothingQuality = "high";
        ctx.save();

        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;

        // Move the crop origin to the canvas origin (0,0)
        ctx.translate(-cropX, -cropY);

        ctx.drawImage(
            image,
            0,
            0,
            image.naturalWidth,
            image.naturalHeight,
            0,
            0,
            image.naturalWidth,
            image.naturalHeight,
        );


        const extension = fileExtn || '.jpg';
        const format = extension === '.png' ? 'image/png' : 'image/jpeg';
        const timestamp = Date.now();
        const fileName = `${timestamp}.${extension}`;
        setFileName(fileName);

        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (!blob) {
                    console.error('Canvas is empty');
                    return;
                }
                blob.name = fileName;
                resolve(blob);
            }, format);
        });
    }

    const handleChange = info => {
        let fileExtn = path.extname(info.file.name).toLowerCase();
        setFileExtn(fileExtn);
    };

    const beforeUpload = (file) => {
        setCroppedImage(null);
        setImageRef(null);
        setVisible(true);
        setSrc(URL.createObjectURL(file));
        // return false to not have it trigger upload, we want the file but no the actual uploading
        return false;
    }

    const getImageDisplayed = () => {
        if (loading) {
            return null;
        }

        if (base64Image) {
            return base64Image
        }

        if (currentUrl) {
            return currentUrl;
        }

        return null;
    }

    const UploadButton = () => {
        return <div style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            {loading ? <LoadingOutlined /> : <PlusOutlined style={{ fontSize: '24px' }} />}
            <div style={{ marginTop: 8 }}>{loading ? 'Saving...' : 'Add Image'}</div>
        </div>
    };

    const ModifyButton = () => {
        return <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            <img
                src={getImageDisplayed()} alt="Image Uploaded"
                style={{ ...imageStyle, opacity: loading ? 0.5 : 1 }}
            />
            <div style={{ 
                position: 'absolute', 
                bottom: '8px', 
                right: '8px',
                display: 'flex',
                gap: '8px',
                zIndex: 10
            }}>
                <Button 
                    size="small"
                    shape="circle"
                    icon={loading ? <LoadingOutlined /> : <DeleteOutlined />} 
                    onClick={handleRemove}
                    style={{ 
                        background: 'white',
                        border: '1px solid #ff4d4f',
                        color: '#ff4d4f',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                />
                <Button 
                    size="small"
                    shape="circle"
                    icon={loading ? <LoadingOutlined /> : <ScissorOutlined />} 
                    onClick={handleRecrop}
                    style={{ 
                        background: 'white',
                        border: '1px solid #1890ff',
                        color: '#1890ff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                />
                <div style={{ 
                    background: 'white',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #ddd',
                }}>
                    {loading ? <LoadingOutlined /> : <EditOutlined style={{ color: '#555', fontSize: '12px' }} />}
                </div>
            </div>
        </div>;
    }

    return (
        <>
            <div className='container mb-0'>
                <div className='col text-center'>
                    <div className='row text-center justify-content-center'>
                        <Upload
                            name="topLogo"
                            listType="picture-card"
                            className={rectangle ? "avatar-uploader template-image-uploader" : "avatar-uploader"}
                            showUploadList={false}
                            customRequest={() => {}}
                            beforeUpload={beforeUpload}
                            onChange={handleChange}
                            disabled={!isLoggedInMemberAdmin || loading}
                        >
                            {getImageDisplayed() || loading ?
                                <ModifyButton />:
                                <UploadButton />
                            }
                        </Upload>

                        <Modal
                            visible={visible}
                            title="Crop your image"
                            onOk={handleOk}
                            onCancel={handleCancel}
                            confirmLoading={loading}
                            footer={[
                                <Button key="back" onClick={handleCancel} disabled={loading}>
                                    Cancel
                                </Button>,
                                <Button key="submit" type="primary" loading={loading} disabled={isCommitButtonDisabled} onClick={handleOk}>
                                    Confirm & Save
                                </Button>,
                            ]}
                        >
                            <div className="cropper">
                                {src && (
                                    <ReactCrop
                                        src={src}
                                        crop={crop}
                                        ruleOfThirds
                                        onImageLoaded={onImageLoaded}
                                        onComplete={onCropComplete}
                                        onChange={onCropChange}
                                        onDragStart={() => setIsDragging(true)}
                                        onDragEnd={() => setIsDragging(false)}
                                        circularCrop={circle}
                                    />
                                )}
                            </div>

                        </Modal>
                    </div>
                </div>
            </div>
        </>);
}


export default UploadImageEditor;
